<template>
  <ContentList
    :columnCollapsed="columnCollapsed"
    v-bind="$attrs"
    :entries="sortedLinks ?? []"
    :loading="
      !legalText || paragraphLoading || chainsLoading || incomingLoading
    "
    :backgroundColor="backgroundColor"
    :toggleExpandButtonStyles="'max-lg:hidden'"
    :titleStyles="'max-lg:mb-2'"
    @delete="deleteLink"
  >
    <template #title>
      <CollapsedLinkIcon v-if="columnCollapsed" class="h-3 w-3" />
      <template v-else>{{ t("references") }}</template>
    </template>
    <template #subtitle>
      <span>{{ t("in", [formattedParagraph]) }}</span>
      <LawVersionIndicator
        class="ms-1 italic"
        :legalTextParagraph="oldParagraph"
      />
    </template>
    <template #list-header-1>
      <span>{{ t("on", [formattedParagraph]) }}</span>
      <LawVersionIndicator
        class="ms-1 italic"
        :legalTextParagraph="oldParagraph"
      />
    </template>
    <template #empty-0>
      {{ t("no_outgoing_references") }}
    </template>
    <template #empty-1>{{ t("no_incoming_references") }}</template>
    <template #item="{ entry, listIndex }">
      <div class="w-full flex-grow overflow-hidden">
        <LinkText
          class="w-full px-2 text-6pt"
          :link="entry"
          :curated="Boolean(entry.curated)"
          :columnCollapsed="columnCollapsed"
          :showAdditionalText="listIndex === 0"
          @click="$emit('navigation')"
        >
          <template v-if="!columnCollapsed" #icon>
            <OutgoingLinkIcon v-if="listIndex === 0" class="h-5 w-5" />
            <IncomingLinkIcon v-else class="h-5 w-5" />
          </template>
        </LinkText>
      </div>
    </template>
  </ContentList>
</template>

<script lang="ts" setup>
import type { ILegalText } from "~/models/ILegalText";
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";
import type { ICuration, ILink } from "~/models/IUserEntry";

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    legalText: ILegalText | undefined;
    legalTextParagraph: ILegalTextParagraph | undefined;
    paragraphLoading: boolean;
    columnCollapsed: boolean;
    backgroundColor?: "grey" | "transparent";
  }>(),
  { backgroundColor: "grey", paragraphLoading: false }
);

defineEmits<{
  navigation: [];
}>();

const { legalTextParagraph } = toRefs(props);

const { oldParagraph } = useOldParagraphState();

const paragraphId = computed(
  () => oldParagraph?.value?.id ?? legalTextParagraph.value?.id
);

const { t } = useI18n({ useScope: "local" });

const {
  data: linkChains,
  query: { isLoading: chainsLoading },
} = useFetchParagraphEntryByType(paragraphId, "link_chain");

const {
  data: incomingLinkChains,
  query: { isLoading: incomingLoading },
} = useFetchIncomingLinkChains(paragraphId);

const { open: deleteLink } = useEntryDeleteModal();

const sortingFn = (a: ILink, b: ILink) => {
  return a.legal_text_paragraph?.slug !== b.legal_text_paragraph?.slug
    ? (a.legal_text_paragraph?.slug ?? "").localeCompare(
        b.legal_text_paragraph?.slug ?? ""
      )
    : (a.legal_text_paragraph?.legal_text?.name ?? "").localeCompare(
        b.legal_text_paragraph?.legal_text?.name ?? ""
      );
};

const sortedLinks = computed<(ILink & ICuration)[][]>(() => {
  return [
    [...(linkChains.value ?? [])]
      .flatMap((chain) =>
        chain.links.map((link) => ({ ...link, curated: chain.curated }))
      )
      .sort(sortingFn),
    [...(incomingLinkChains.value ?? [])]
      .flatMap((chain) =>
        chain.links
          .map((link) => ({ ...link, curated: chain.curated }))
          .filter(
            ({ legal_text_paragraph_linked_id }) =>
              legal_text_paragraph_linked_id === paragraphId.value
          )
      )
      .sort(sortingFn),
  ];
});

const formattedParagraph = computed(() => {
  let text = props.legalTextParagraph?.paragraph ?? "";

  if (props.columnCollapsed) {
    text = text.replace(/Art(\w+)?/, "").replace(/§+/, "");
  }

  if (props.legalText) {
    text = `${text} ${props.legalText.name}`;
  }

  return text.trim();
});

useProvideParagraphPreview(false);
</script>

<i18n>
{
  "de": {
    "references": "Verweise",
    "in": "in {0}",
    "to": "zu {0}",
    "on": "auf {0}",
    "no_incoming_references": "Keine eingehenden Verweise.",
    "no_outgoing_references": "Keine ausgehenden Verweise."
  },
  "en": {
    "references": "References",
    "in": "in {0}",
    "on": "to {0}",
    "to": "to {0}",
    "no_incoming_references": "No incoming references",
    "no_outgoing_references": "No outgoing references"
  }
}
</i18n>
